import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';

import { AuthProvider, useAuth } from './context/AuthContext';
import LoginScreen from './screens/LoginScreen';
import TwoFAScreen from './screens/TwoFAScreen';
import SetupPasswordScreen from './screens/SetupPasswordScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import PrivacyNoticeScreen from './screens/PrivacyNoticeScreen';
import PrivacyNoticePage from './screens/PrivacyNoticePage';
import SetupTokenScreen from './screens/SetupTokenScreen';
import SetupInstallScreen from './screens/SetupInstallScreen';
import HomeScreen from './screens/HomeScreen';
import GateModeScreen from './screens/GateModeScreen';
import SignOutScreen from './screens/SignOutScreen';
import ResultScreen from './screens/ResultScreen';
import SightingFormScreen from './screens/SightingFormScreen';
import SightingConfirmationScreen from './screens/SightingConfirmationScreen';
import BannedVehicleScreen from './screens/BannedVehicleScreen';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import OfficersPage from './pages/admin/OfficersPage';
import SitesPage from './pages/admin/SitesPage';
import SightingsPage from './pages/admin/SightingsPage';
import KnownVehiclesPage from './pages/admin/KnownVehiclesPage';
import StatsPage from './pages/admin/StatsPage';
import AccountPage from './pages/admin/AccountPage';
import SuperAdminLayout from './components/superadmin/SuperAdminLayout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SACompaniesPage from './pages/superadmin/SACompaniesPage';
import SAOfficersPage from './pages/superadmin/SAOfficersPage';
import SASightingsPage from './pages/superadmin/SASightingsPage';
import SAVehiclesPage from './pages/superadmin/SAVehiclesPage';
import SAStatsPage from './pages/superadmin/SAStatsPage';
import SAAuditLogPage from './pages/superadmin/SAAuditLogPage';
import SAAccountPage from './pages/superadmin/SAAccountPage';
import SASubjectRequestsPage from './pages/superadmin/SASubjectRequestsPage';
import './App.css';

// Nuclear sign-out escape hatch.
// Visit any URL with ?force_signout=1 to wipe all local state and Clerk session.
// Useful when Clerk cookies persist across Safari clears or PWA reinstalls.
function ForceSignoutHandler({ children }) {
  const clerkAuth = useClerkAuth();
  const [clearing] = useState(
    () => new URLSearchParams(window.location.search).get('force_signout') === '1'
  );

  useEffect(() => {
    if (!clearing) return;

    // Absolute last-resort fallback: redirect no matter what after 4 seconds
    const fallbackTimer = setTimeout(() => {
      window.location.href = '/login';
    }, 4000);

    async function nuke() {
      try { await clerkAuth.signOut(); } catch (_) {}
      try { localStorage.clear(); } catch (_) {}
      try { sessionStorage.clear(); } catch (_) {}
      try {
        document.cookie.split(';').forEach(c => {
          const name = c.split('=')[0].trim();
          if (!name) return;
          const base = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = base;
          document.cookie = `${base};domain=${window.location.hostname}`;
        });
      } catch (_) {}
      clearTimeout(fallbackTimer);
      window.location.href = '/login';
    }

    nuke().catch(() => {
      clearTimeout(fallbackTimer);
      window.location.href = '/login';
    });

    return () => clearTimeout(fallbackTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (clearing) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0f1e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', letterSpacing: '1px' }}>
          Clearing session…
        </div>
        <a
          href="/login"
          style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', textDecoration: 'underline' }}
        >
          Tap here if this takes too long
        </a>
      </div>
    );
  }

  return children;
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isInitializing, needsPrivacyNotice } = useAuth();

  // While initializing (checking Clerk auth state), show loading
  if (isInitializing) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  // Privacy notice acceptance required before accessing any protected route
  if (needsPrivacyNotice) {
    return <PrivacyNoticeScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// PF-003 fix: role-aware landing. The / route must send each role to its
// own home — OFFICER stays on HomeScreen, COMPANY_ADMIN goes to /admin,
// SUPER_ADMIN goes to /super-admin. Previously every role landed on the
// officer HomeScreen.
function RoleLanding() {
  const { user } = useAuth();

  if (user?.role === 'SUPER_ADMIN') {
    return <Navigate to="/super-admin" replace />;
  }
  if (user?.role === 'COMPANY_ADMIN') {
    return <Navigate to="/admin" replace />;
  }
  // OFFICER and INDEPENDENT_OFFICER use the operator HomeScreen
  return <HomeScreen />;
}

// Admin route wrapper - checks for COMPANY_ADMIN role only
function AdminRoute({ children }) {
  const { isAuthenticated, isInitializing, needsPrivacyNotice, user } = useAuth();

  if (isInitializing) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (needsPrivacyNotice) {
    return <PrivacyNoticeScreen />;
  }

  if (isAuthenticated && !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'COMPANY_ADMIN') {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Super Admin route wrapper - checks for SUPER_ADMIN role only
function SuperAdminRoute({ children }) {
  const { isAuthenticated, isInitializing, needsPrivacyNotice, user } = useAuth();

  if (isInitializing) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (needsPrivacyNotice) {
    return <PrivacyNoticeScreen />;
  }

  if (isAuthenticated && !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public route wrapper
// Public route wrapper (redirect if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function ProfileScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-vcin-bg flex flex-col items-center justify-center px-6">
      <h1 className="text-2xl font-bold text-vcin-text mb-4">Profile</h1>
      <p className="text-vcin-secondary mb-2">{user?.full_name || user?.email}</p>
      <p className="text-vcin-secondary mb-6">{user?.role}</p>
      <button
        onClick={() => navigate('/signout')}
        className="h-12 px-6 bg-white border rounded-xl text-vcin-alert-red font-semibold hover:bg-red-50 transition-colors"
        style={{ borderColor: '#D8DCE4' }}
        data-testid="officer-logout-btn"
      >
        Sign Out
      </button>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginScreen />
        </PublicRoute>
      } />
      <Route path="/verify-2fa" element={
        <TwoFAScreen />
      } />
      <Route path="/setup-password" element={
        <SetupPasswordScreen />
      } />
      <Route path="/forgot-password" element={
        <ForgotPasswordScreen />
      } />
      <Route path="/reset-password" element={
        <ResetPasswordScreen />
      } />
      <Route path="/privacy" element={<PrivacyNoticePage />} />
      <Route path="/setup/install" element={<SetupInstallScreen />} />
      <Route path="/setup/:token" element={<SetupTokenScreen />} />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <RoleLanding />
        </ProtectedRoute>
      } />
      <Route path="/result" element={
        <ProtectedRoute>
          <ResultScreen />
        </ProtectedRoute>
      } />
      <Route path="/log-sighting" element={
        <ProtectedRoute>
          <SightingFormScreen />
        </ProtectedRoute>
      } />
      <Route path="/sighting-confirmation" element={
        <ProtectedRoute>
          <SightingConfirmationScreen />
        </ProtectedRoute>
      } />
      <Route path="/banned-vehicle" element={
        <ProtectedRoute>
          <BannedVehicleScreen />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfileScreen />
        </ProtectedRoute>
      } />
      <Route path="/gate-mode" element={
        <ProtectedRoute>
          <GateModeScreen />
        </ProtectedRoute>
      } />
      <Route path="/signout" element={<SignOutScreen />} />
      
      {/* Admin routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="officers" element={<OfficersPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="sightings" element={<SightingsPage />} />
        <Route path="known-vehicles" element={<KnownVehiclesPage />} />
        <Route path="statistics" element={<StatsPage />} />
        <Route path="account" element={<AccountPage />} />
      </Route>
      
      {/* Super Admin routes */}
      <Route path="/super-admin" element={
        <SuperAdminRoute>
          <SuperAdminLayout />
        </SuperAdminRoute>
      }>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="companies" element={<SACompaniesPage />} />
        <Route path="officers" element={<SAOfficersPage />} />
        <Route path="sightings" element={<SASightingsPage />} />
        <Route path="vehicles" element={<SAVehiclesPage />} />
        <Route path="statistics" element={<SAStatsPage />} />
        <Route path="audit-log" element={<SAAuditLogPage />} />
        <Route path="subject-requests" element={<SASubjectRequestsPage />} />
        <Route path="account" element={<SAAccountPage />} />
      </Route>
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ForceSignoutHandler>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ForceSignoutHandler>
      </BrowserRouter>
    </div>
  );
}

export default App;
